import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, Button, Link as MuiLink, Skeleton, Stack, Typography } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { Link as RouterLink } from 'react-router';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type LayoutProps = PropsWithChildren<{
  actions?: React.ReactNode;
}>;

export const RowDetailsLayout: React.FC<LayoutProps> = ({ actions, children }) => (
  <Stack spacing={1.5}>
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { md: 'repeat(auto-fit, minmax(240px, 1fr))', xs: '1fr' },
      }}
    >
      {children}
    </Box>
    {actions && (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {actions}
      </Stack>
    )}
  </Stack>
);

type SectionProps = PropsWithChildren<{
  count?: number;
  title: string;
  wide?: boolean;
}>;

export const RowDetailsSection: React.FC<SectionProps> = ({ children, count, title, wide }) => (
  <Stack spacing={1} sx={{ gridColumn: wide ? { md: 'span 2' } : undefined, minWidth: 0 }}>
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Typography
        sx={{ color: themeColors.greyDark, letterSpacing: 0.4, textTransform: 'uppercase' }}
        variant="f12Medium"
      >
        {title}
      </Typography>
      {count !== undefined && count > 0 && (
        <Box
          component="span"
          sx={{
            backgroundColor: themeColors.accentColor10,
            borderRadius: '999px',
            color: themeColors.accentColorDark,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '18px',
            minWidth: 18,
            px: 0.75,
            textAlign: 'center',
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
    {children}
  </Stack>
);

type ItemProps = PropsWithChildren<{
  label: string;
}>;

export const RowDetailsItem: React.FC<ItemProps> = ({ children, label }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
    <Typography sx={{ color: themeColors.grey }} variant="f12Regular">
      {label}
    </Typography>
    <Box sx={{ color: themeColors.greyDarker, minWidth: 0, typography: 'f14Regular' }}>
      {children}
    </Box>
  </Stack>
);

export const RowDetailsText: React.FC<{ lines?: number; text?: string }> = ({
  lines = 4,
  text,
}) => {
  if (!text?.trim()) {
    return <RowDetailsMuted text="—" />;
  }
  return (
    <Typography
      sx={{
        color: themeColors.greyDarker,
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
      title={text}
      variant="f14Regular"
    >
      {text}
    </Typography>
  );
};

export const RowDetailsMuted: React.FC<{ text: string }> = ({ text }) => (
  <Typography sx={{ color: themeColors.grey }} variant="f14Regular">
    {text}
  </Typography>
);

export const RowDetailsAllDone: React.FC<{ text: string }> = ({ text }) => (
  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
    <CheckCircleOutlineIcon sx={{ color: themeColors.green, fontSize: 18 }} />
    <Typography sx={{ color: themeColors.greyDark }} variant="f14Regular">
      {text}
    </Typography>
  </Stack>
);

export const RowDetailsLoading: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <Stack spacing={0.75}>
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton
        height={18}
        key={index}
        variant="rounded"
        width={index === rows - 1 ? '60%' : '100%'}
      />
    ))}
  </Stack>
);

export const RowDetailsError: React.FC = () => {
  const { t } = useTranslationWithPrefix('components.listDetails.row');
  return <RowDetailsMuted text={t('error')} />;
};

export const RowDetailsMore: React.FC<{ count: number; to?: string }> = ({ count, to }) => {
  const { t } = useTranslationWithPrefix('components.listDetails.row');
  if (count <= 0) {
    return null;
  }
  const label = t('more', { count });
  if (!to) {
    return <RowDetailsMuted text={label} />;
  }
  return <RowDetailsLink to={to}>{label}</RowDetailsLink>;
};

type LinkProps = PropsWithChildren<{
  to: string;
}>;

export const RowDetailsLink: React.FC<LinkProps> = ({ children, to }) => (
  <MuiLink
    component={RouterLink}
    onClick={event => event.stopPropagation()}
    sx={{
      '&:hover': { textDecoration: 'underline' },
      color: themeColors.accentColorDark,
      minWidth: 0,
      textDecoration: 'none',
      typography: 'f14Regular',
    }}
    to={to}
    viewTransition
  >
    {children}
  </MuiLink>
);

type ContactLinkProps = {
  kind: 'email' | 'phone';
  value: string;
};

export const RowDetailsContactLink: React.FC<ContactLinkProps> = ({ kind, value }) => {
  const Icon = kind === 'phone' ? PhoneOutlinedIcon : MailOutlineIcon;
  const href = kind === 'phone' ? `tel:${value.replace(/[^\d+]/g, '')}` : `mailto:${value}`;
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Icon sx={{ color: themeColors.grey, fontSize: 16 }} />
      <MuiLink
        href={href}
        noWrap
        onClick={event => event.stopPropagation()}
        sx={{
          '&:hover': { textDecoration: 'underline' },
          color: themeColors.greyDarker,
          textDecoration: 'none',
          typography: 'f14Regular',
        }}
      >
        {value}
      </MuiLink>
    </Stack>
  );
};

type OpenButtonProps = {
  label: string;
  to: string;
};

export const RowDetailsOpenButton: React.FC<OpenButtonProps> = ({ label, to }) => (
  <Button
    component={RouterLink}
    endIcon={<ArrowForwardIcon />}
    onClick={(event: React.MouseEvent) => event.stopPropagation()}
    size="small"
    sx={{ color: themeColors.accentColorDark }}
    to={to}
    variant="outlined"
    viewTransition
  >
    {label}
  </Button>
);
